import { Link } from "react-router-dom";
import { BalderAssistant } from "../components/balder-assistant";
import {
  MktAccent,
  MktBadgeRow,
  MktBalderShowcase,
  MktButton,
  MktCtaBand,
  MktFaq,
  MktFaqItem,
  MktFeature,
  MktHero,
  MktPillar,
  MktPrice,
  MktSection,
  MktTrustItem,
} from "../components/marketing";
import { appEnv } from "../lib/config";
import { Seo } from "../lib/seo";

/**
 * Marketing home page for the ODIN apex site (odinforge.io).
 *
 * TRUTHFULNESS RULES (enforced by Landing.test.tsx and the shared marketing
 * checks): no fabricated testimonials, customer / community / server counts,
 * uptime guarantees, SLAs, "99.9%", "trusted by" or certifications; and no claim
 * that an unfinished capability is already live. The free tier is genuinely €0;
 * Core and Pro carry the approved, VAT-inclusive monthly prices (€20 / €50) and
 * Dedicated is quoted ("From €150") rather than fixed. The availability note
 * states plainly that we make "no uptime promises".
 *
 * The public nav + footer are provided by <MarketingLayout>; this component owns
 * only the page content.
 */
export function Landing(): JSX.Element {
  return (
    <>
      <Seo
        title="ODIN — run your Discord server from a calm control plane"
        description="Design roles, channels, onboarding, moderation and automations, preview every change, and deploy only when you approve — with BALDER, your AI companion. Free to start."
        path="/"
      />
      <MktHero
        kicker="Discord control plane"
        title={
          <>
            Run your Discord server from a <MktAccent>calm control plane</MktAccent>
          </>
        }
        subhead="ODIN is a separate web dashboard that connects to your Discord server and sets up an all-in-one management bot for you — no coding, no bot token, no hosting. Design your roles, channels, onboarding, moderation and automations, preview exactly what will change, and deploy only when you approve, with BALDER, an AI assistant, explaining every step."
        backdrop="/brand/odin/odin-banner.webp"
      >
        <MktButton to="/login" variant="primary">
          Start free
        </MktButton>
        <MktButton href="#how-it-works" variant="secondary">
          See how ODIN works
        </MktButton>
        <MktBadgeRow
          items={["Free to start", "No credit card", "No coding needed", "Cancel anytime"]}
        />
      </MktHero>

      <MktSection
        kicker="New to ODIN?"
        title="ODIN is not Discord — it connects to it"
        lead="Discord is where your community lives. ODIN is a separate web service you sign in to that connects to your existing Discord server and manages it for you. You keep your own Discord account and your own server; nothing is replaced."
        id="new-to-odin"
      >
        <div className="mkt-pillars">
          <MktPillar
            title="Create your ODIN account"
            desc="Sign up on the web with Discord or Google — free, no credit card, no Discord developer setup."
          />
          <MktPillar
            title="Connect your Discord server"
            desc="Approve ODIN through Discord's official 'Add to Server' screen. ODIN's all-in-one bot is added for you — you never create or paste a bot token."
          />
          <MktPillar
            title="Design, review, deploy"
            desc="Shape changes as a blueprint, preview a full dry run, and apply only when you approve. BALDER explains each step in plain language."
          />
        </div>
      </MktSection>

      <MktSection
        kicker="Why ODIN"
        title="Editing a live server by hand is a gamble"
        lead="One wrong permission, a deleted category, a botched role hierarchy — on Discord those changes are instant, public, and hard to undo. ODIN removes the gamble."
      >
        <div className="mkt-grid">
          <MktFeature
            title="Without ODIN"
            desc="You click through Discord settings live, hope you remembered every dependency, and find out in front of your whole community when something breaks. Rolling back means reconstructing it from memory."
          />
          <MktFeature
            title="With ODIN"
            desc="You shape changes as a blueprint, see a full dry-run of what would be created, changed or removed, and apply it only after you approve. Every release is snapshotted so you can roll back."
          />
        </div>
      </MktSection>

      <MktSection
        kicker="How it works"
        title="Design, review, deploy — in that order"
        lead="ODIN turns server changes into a deliberate workflow. You shape what you want, see exactly what would happen, and only then apply it."
        id="how-it-works"
      >
        <div className="mkt-pillars">
          <MktPillar
            title="Design"
            desc="Shape roles, channels, categories and onboarding as a versioned blueprint — starting from a template or from your live server."
          />
          <MktPillar
            title="Review"
            desc="ODIN produces a dry run showing exactly what would be created, changed or removed. BALDER explains it in plain language."
          />
          <MktPillar
            title="Deploy"
            desc="When you approve, the change is applied to your server. Live deployment stays locked until you deliberately enable it."
          />
          <MktPillar
            title="Roll back"
            desc="Keep snapshots and release reports so you can review what changed and roll a release back if you need to."
          />
        </div>
      </MktSection>

      <MktSection
        kicker="Capabilities"
        title="Everything to run one community, without the rush"
        lead="ODIN focuses on doing one thing well: helping you shape and operate a single Discord server safely."
      >
        <div className="mkt-grid">
          <MktFeature
            title="One server, managed carefully"
            desc="Roles, channels, onboarding and moderation for your community — designed, reviewed and operated from a single calm place."
          />
          <MktFeature
            title="ODIN's all-in-one bot, added for you"
            desc="Connect your server through Discord's official authorization and ODIN adds its managed bot — no developer portal, no bot token, no hosting. Bringing your own bot identity instead is available only on the Dedicated plan."
          />
          <MktFeature
            title="BALDER, your AI assistant"
            desc="BALDER can inspect your server, prepare blueprint drafts and produce dry-run previews. It advises and never deploys or approves changes on its own."
          />
          <MktFeature
            title="Moderation & onboarding"
            desc="Configure moderation, automated moderation and member onboarding flows — reviewed the same careful way as any other change before they take effect."
          />
          <MktFeature
            title="Automations & integrations"
            desc="Connect n8n automations and content sources such as RSS, streaming and game-server notifications, with credentials stored server-side and never shown back to the browser."
          />
          <MktFeature
            title="Billing you can read"
            desc="Free, Core, Pro and Dedicated plans gate features through clear entitlements. All prices include VAT, and every plan includes a limited BALDER AI allowance."
          />
        </div>
      </MktSection>

      <MktBalderShowcase />

      <MktSection
        kicker="Safety"
        title="Nothing goes live without your approval"
        lead="Safety is ODIN's real differentiator. Every structural change flows through review-and-apply, so surprises don't reach your community."
        id="safety"
      >
        <div className="mkt-trust">
          <MktTrustItem
            title="Approval required"
            desc="Nothing structural reaches your server without an explicit approval from you. BALDER can prepare and explain, but never approves on its own."
          />
          <MktTrustItem
            title="Fully reversible"
            desc="Every change is staged as a dry run first, and snapshots plus release reports let you roll a release back. A change is never a one-way door."
          />
          <MktTrustItem
            title="No token to manage"
            desc="You connect ODIN through Discord's official authorization, so there is no bot token to copy, paste, or keep safe. (If you bring your own bot on the Dedicated plan, any token you provide is encrypted, stored server-side, and never shown again — and should never be shared in chat, tickets, or screenshots.)"
          />
          <MktTrustItem
            title="A clear record"
            desc="Actions are recorded so you can see what changed, when, and who approved it — a straightforward audit trail for your community."
          />
        </div>
      </MktSection>

      <MktCtaBand
        kicker="Free plan · €0"
        title="Start free — no credit card required"
        lead="The Free plan gives you one server, connected through ODIN's all-in-one bot, with full design and safe dry-run review. Explore everything at your own pace and upgrade only if you outgrow it."
        note="Free to start · No credit card · No coding needed · Cancel anytime"
      >
        <MktButton to="/login" variant="primary">
          Start free
        </MktButton>
        <MktButton to="/pricing" variant="secondary">
          Compare plans
        </MktButton>
      </MktCtaBand>

      <MktSection
        kicker="Pricing"
        title="Start free, upgrade when you need more"
        lead="Every plan lists what it includes and its main limits. All prices include VAT; Core and Pro have clear monthly prices, and Dedicated is quoted to fit your setup."
      >
        <div className="mkt-priceteaser">
          <MktPrice name="Free" amount="€0" desc="One server, ODIN's all-in-one bot, design and safe dry-run review." />
          <MktPrice name="Core" amount="€20" desc="More capabilities and integrations for an active community." />
          <MktPrice name="Pro" amount="€50" desc="For growing communities that need the full toolkit." />
          <MktPrice name="Dedicated" amount="From €150" desc="Hands-on setup for teams that want extra support." />
        </div>
        <div className="mkt-hero__actions">
          <MktButton to="/pricing" variant="secondary">
            View full pricing
          </MktButton>
        </div>
      </MktSection>

      <MktSection
        kicker="Questions"
        title="Honest answers for the skeptical"
        lead="The things people actually ask before connecting a server they care about."
      >
        <MktFaq>
          <MktFaqItem question="Is it really free?" defaultOpen>
            <p>
              Yes. The Free plan is €0 — no trial timer and no credit card. It covers one server, connected
              through ODIN's all-in-one bot, and full design plus safe dry-run review. Paid plans exist for
              bigger needs, but you're never pushed onto one — Core and Pro have clear, VAT-inclusive prices
              and Dedicated is quoted to fit your setup.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Is it safe to connect my server?">
            <p>
              That's the whole point of ODIN. Nothing structural is applied to your server until you review
              a dry run and explicitly approve it. Live deployment is locked by default and is a deliberate,
              opt-in step. BALDER can prepare and explain changes, but it can never deploy or approve on its
              own.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Do I need a bot token or a Discord developer account?">
            <p>
              No. ODIN uses its own all-in-one bot, added to your server through Discord's official
              &ldquo;Add to Server&rdquo; authorization — you never open the Discord developer portal or
              handle a bot token. Bringing your own bot identity instead is available only on the Dedicated
              plan; any token you provide there is encrypted, stored server-side, and never shown again.
              Never share a bot token in chat, support tickets, or screenshots.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Can ODIN break my server?">
            <p>
              ODIN is built to prevent exactly that. You always see a full plan — what is added, changed and
              removed — before anything happens, and every release is snapshotted so you can roll it back.
              A change is never a one-way door.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Can I cancel anytime?">
            <p>
              Yes. The Free plan carries no commitment, and paid plans can be cancelled whenever you like.
              Your Discord server and its data stay yours throughout.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Do I need to be technical?">
            <p>
              No. ODIN is designed to be beginner-friendly: guided onboarding, templates to start from,
              plain-language previews, and BALDER on hand to explain each step. If you can run a Discord
              community, you can use ODIN.
            </p>
          </MktFaqItem>
        </MktFaq>
      </MktSection>

      <MktSection kicker="Current availability" title="Honest about where we are">
        <p className="mkt-section__lead">
          ODIN is being rolled out carefully. This is the <span className="mkt-mono">{appEnv}</span>{" "}
          environment. Live deployment to a real Discord server is a deliberate, opt-in step and remains
          locked by default. We make no uptime promises; see the <Link to="/status">status page</Link>{" "}
          for the platform's current state.
        </p>
      </MktSection>

      <MktCtaBand
        title="Start free today"
        lead="Create an account, connect your Discord server, and preview every change before it goes live. Nothing deploys without your approval."
        note="Free to start · No credit card · Cancel anytime"
      >
        <MktButton to="/login" variant="primary">
          Start free
        </MktButton>
        <MktButton to="/pricing" variant="secondary">
          View pricing
        </MktButton>
      </MktCtaBand>

      <BalderAssistant />
    </>
  );
}
